@if (!error) {
  <mat-table
    matSort
    matSortActive="label"
    matSortDisableClear="true"
    matSortStart="asc"
    [dataSource]="dataSource"
    [formGroup]="filters"
    [trackBy]="getProjectId"
  >
    <ng-container matColumnDef="progress">
      <mat-cell *matCellDef="let element" />
      <mat-header-cell *matHeaderCellDef [attr.colspan]="projectColumns.length">
        @if (projectQuery.isFetching()) {
          <fw-progress-bar />
        }
      </mat-header-cell>
    </ng-container>
    <ng-container matColumnDef="lock">
      <mat-header-cell *matHeaderCellDef />
      <mat-cell *matCellDef="let element">
        <project-lock-status [project]="element" />
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="label">
      <mat-header-cell *matHeaderCellDef class="full-width" mat-sort-header>
        <!-- keydown and click listeners are to stop the sort header from stealing the event -->
        <fw-text-input
          formControlName="project"
          placeholder="Project"
          size="small"
          test-id="project-label-filter"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
        />
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a
          class="ellipsis"
          fwTooltipPosition="above"
          href="#/projects/{{ project._id }}/description"
          test-id="project-label"
          [fwTooltip]="project.label"
        >
          {{ project.label }}
        </a>
        <ng-container *featureIf="['ohif_react']">
          <mat-icon
            *permissionIf="'files_view_contents'; project: project._id"
            class="load-ohif xs"
            fwTooltip="Viewer Study List"
            fwTooltipPosition="right"
            (click)="viewProjectStudies(project)"
          >
            panorama
          </mat-icon>
        </ng-container>
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="group">
      <mat-header-cell *matHeaderCellDef class="full-width" mat-sort-header>
        <!-- keydown and click listeners are to stop the sort header from stealing the event -->
        <fw-text-input
          formControlName="group"
          placeholder="Group"
          size="small"
          test-id="group-label-filter"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
        />
      </mat-header-cell>
      <mat-cell *matCellDef="let project" test-id="group-label">
        <span class="ellipsis" fwTooltipPosition="above" [fwTooltip]="project?.group_info?.label">
          {{ project?.group_info?.label || project.group }}
        </span>
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="subject_count">
      <mat-header-cell *matHeaderCellDef mat-sort-header> Subjects </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a
          href="#/projects/{{ project._id }}/subjects"
          test-id="subject-count"
          [ngClass]="{ 'muted-text': !project.stats?.number_of.subjects }"
          >{{ project.stats?.number_of.subjects || 0 }}</a
        >
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="session_count">
      <mat-header-cell *matHeaderCellDef mat-sort-header> Sessions </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a
          href="#/projects/{{ project._id }}/sessions"
          test-id="session-count"
          [ngClass]="{ 'muted-text': !project.stats?.number_of.sessions }"
        >
          {{ project.stats?.number_of.sessions || 0 }}
          @if (project.stats?.number_of.noncompliant_sessions) {
            <mat-icon class="flag xs" fwTooltipPosition="above" [fwTooltip]="getFlaggedTooltip(project)">flag</mat-icon>
          }
        </a>
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="roles">
      <mat-header-cell *matHeaderCellDef>
        <fw-multi-select
          maxOptionsHeight="400px"
          optionsWidth="320px"
          placeholder="Roles"
          showFilter="false"
          size="small"
          titleProperty="label"
          valueProperty="_id"
          width="100px"
          [filterItemsOnSelect]="false"
          [formControl]="filters.controls.roles"
          [maxSelectedShown]="0"
          [options]="projectRolesQuery.data()?.results || []"
          [showClear]="true"
          [useCheckbox]="true"
        />
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a
          class="ellipsis"
          href="#/projects/{{ project._id }}/permissions"
          test-id="project-roles"
          [fwTooltip]="getUsersRoleLabel(project)"
        >
          {{ getUsersRoleLabel(project) }}
        </a>
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="users">
      <mat-header-cell *matHeaderCellDef>
        <fw-multi-select
          maxOptionsHeight="300px"
          optionsWidth="fit-content"
          placeholder="Users"
          size="small"
          [filterItemsOnSelect]="false"
          [formControl]="filters.controls.users"
          [maxSelectedShown]="0"
          [showClear]="true"
        >
          @for (user of userOptions(); track user) {
            <fw-menu-item [title]="user.firstname + ' ' + user.lastname" [value]="user._id">
              <fw-avatar
                size="medium"
                variant="circular"
                [colorSeed]="user | userInitial"
                [content]="user | userAvatarContent"
                [imageUrl]="user | userAvatarUrl"
                [initial]="user | userInitial"
              />
            </fw-menu-item>
          }
        </fw-multi-select>
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <fw-avatar-list [routerLink]="[project._id, 'permissions']">
          @for (user of project.permissions; track user._id) {
            <fw-avatar
              size="medium"
              [colorSeed]="user | userInitial"
              [content]="user | userAvatarContent"
              [fwTooltip]="user.firstname + ' ' + user.lastname"
              [imageUrl]="user | userAvatarUrl"
              [initial]="user | userInitial"
            />
          } @empty {
            <span class="muted-text" ngProjectAs="fw-avatar">No Users</span>
          }
        </fw-avatar-list>
      </mat-cell>
    </ng-container>
    <ng-container matColumnDef="actions">
      <mat-header-cell *matHeaderCellDef>
        <button color="primary" mat-flat-button test-id="new-project-button" (click)="newProject()">New Project</button>
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <app-ldap-indicator *featureIf="['ldap_sync']" [ldapStatus]="project.ldap_sync" />
        <div class="list-action-buttons">
          <fw-icon-button
            fwMenuRegister
            fwTooltip="Project Actions"
            icon="menu-vertical"
            test-id="project-actions-button"
            [fwMenuTriggerFor]="actionMenu"
          />
          <ng-template #actionMenu>
            <fw-menu-container>
              <fw-menu>
                @for (menuItem of actionMenuItems; track menuItem.label) {
                  <ng-container *featureIf="menuItem.features; type: 'any'">
                    <a test-id="action-menu-item" [routerLink]="[project._id, menuItem.href]">
                      <fw-menu-item
                        *permissionIf="menuItem.permissions; project: project._id; type: 'any'"
                        [title]="menuItem.label"
                      />
                    </a>
                  </ng-container>
                }
              </fw-menu>
            </fw-menu-container>
          </ng-template>
        </div>
      </mat-cell>
    </ng-container>
    <div *matNoDataRow class="empty-state" test-id="empty-state">
      @if (!projectQuery.isLoading()) {
        @if (filtersActive()) {
          <fw-layout-context
            description="Adjust your search or filter to find existing projects or click `Create New Project` to get started"
            icon="search-loop"
            title="No Projects Found"
          >
            <fw-button test-id="clear-filters-button" variant="outline" (click)="filters.reset()"
              >Clear Filters</fw-button
            >
            <fw-button leftIcon="add-new-create" test-id="new-project-empty-button" (click)="newProject()"
              >New Project</fw-button
            >
          </fw-layout-context>
        } @else {
          <fw-layout-context
            description="It looks like you don't have access to any projects yet. If you'd like to create one, click `Create New Project` to get started!"
            icon="list-paragraph"
            title="No Projects Exist"
          >
            <fw-button test-id="create-project-button" (click)="newProject()">Create New Project</fw-button>
          </fw-layout-context>
        }
      }
    </div>
    <mat-header-row *matHeaderRowDef="projectColumns; sticky: true" />
    <!-- Extra header row for displaying the loading bar -->
    <mat-header-row *matHeaderRowDef="['progress']; sticky: true" class="loading-row" />
    <mat-row *matRowDef="let row; columns: projectColumns" class="list-action-hover" />
  </mat-table>
  <mat-paginator
    [hidePageSize]="true"
    [length]="projectQuery.data()?.total || 0"
    [pageIndex]="page() - 1"
    [pageSize]="pageSize"
    (page)="onPageChange($event)"
  />
} @else {
  <div class="blank-state" test-id="error-state">
    <mat-icon>error_outline</mat-icon>
    <p>Projects could not be retrieved.</p>
    <a class="btn btn-primary" test-id="reload-projects-button">Reload Projects</a>
  </div>
}

<ng-template #loadingState>
  <div class="blank-state" test-id="loading-state">
    <img src="/assets/images/loading.svg" style="margin-bottom: 10px" />
    <p>Loading Projects</p>
  </div>
</ng-template>

<ng-template #noAccess>
  <em class="muted-text">No Access</em>
</ng-template>
